import type { Database } from "@excel-center/supabase";
import { z } from "zod";

type DBStudentCareerCluster = Database["public"]["Enums"]["student_career_cluster"];

export const StudentCareerCluster = {
	ALL: "all",
	ADVANCED_MANUFACTURING: "advanced_manufacturing",
	ADVANCED_MANUFACTURING_ARCHITECTURE_AND_CONSTRUCTION: "advanced_manufacturing_architecture_and_construction",
	ADVANCED_MANUFACTURING_AND_AGRICULTURE_FOOD_AND_NATURAL_RESOURCES: "adv_manufacturing_and_ag_food_natural_resources",
	ADVANCED_MANUFACTURING_AGRICULTURE_FOOD_AND_NATURAL_RESOURCES_ARCHITECTURE_AND_CONSTRUCTION_AND_TRANSPORTATION:
		"adv_manufacturing_ag_food_nat_res_arch_construction_transport",
	ADVANCED_MANUFACTURING_AGRICULTURE_FOOD_AND_NATURAL_RESOURCES_AND_ARCHITECTURE_AND_CONSTRUCTION:
		"adv_manufacturing_ag_food_nat_res_and_arch_construction",
	AGRICULTURE_FOOD_AND_NATURAL_RESOURCES: "agriculture_food_and_natural_resources",
	AGRICULTURE_FOOD_AND_NATURAL_RESOURCES_AND_ARCHITECTURE_AND_CONSTRUCTION: "ag_food_natural_resources_and_architecture_construction",
	AGRICULTURE_FOOD_AND_NATURAL_RESOURCES_AND_BUSINESS_MANAGEMENT_AND_ADMINISTRATION: "ag_food_natural_resources_and_business_mgmt_admin",
	AGRICULTURE_FOOD_AND_NATURAL_RESOURCES_LAW_PUBLIC_SAFETY_CORRECTIONS_AND_SECURITY_AND_TRANSPORTATION:
		"ag_food_nat_res_law_safety_corrections_security_transportation",
	ARCHITECTURE_AND_CONSTRUCTION: "architecture_and_construction",
	ARCHITECTURE_AND_CONSTRUCTION_AND_STEM: "architecture_and_construction_and_stem",
	ARTS_AUDIO_VISUAL_TECHNOLOGY_AND_COMMUNICATIONS: "arts_audio_visual_technology_and_communications",
	BUSINESS_MANAGEMENT_AND_ADMINISTRATION: "business_management_and_administration",
	BUSINESS_MANAGEMENT_AND_ADMINISTRATION_AND_FINANCE: "business_management_and_administration_and_finance",
	BUSINESS_MANAGEMENT_AND_ADMINISTRATION_AND_MARKETING_DISTRIBUTION_AND_LOGISTICS: "business_mgmt_admin_and_marketing_dist_logistics",
	BUSINESS_MANAGEMENT_AND_ADMINISTRATION_FINANCE_AND_MARKETING_DISTRIBUTION_AND_LOGISTICS:
		"business_mgmt_admin_finance_and_marketing_dist_logistics",
	EDUCATION_AND_TRAINING: "education_and_training",
	EDUCATION_AND_TRAINING_AND_HEALTH_SCIENCE: "education_and_training_and_health_science",
	FINANCE: "finance",
	HEALTH_SCIENCE: "health_science",
	HEALTH_SCIENCE_AND_HUMAN_SERVICES: "health_science_and_human_services",
	HEALTH_SCIENCE_AND_LAW_PUBLIC_SAFETY_CORRECTIONS_AND_SECURITY: "health_science_and_law_public_safety_corrections_and_security",
	HOSPITALITY_AND_TOURISM: "hospitality_and_tourism",
	HOSPITALITY_AND_TOURISM_AND_HUMAN_SERVICES: "hospitality_and_tourism_and_human_services",
	HUMAN_SERVICES: "human_services",
	INFORMATION_TECHNOLOGY: "information_technology",
	LAW_PUBLIC_SAFETY_CORRECTIONS_AND_SECURITY: "law_public_safety_corrections_and_security",
	MARKETING_DISTRIBUTION_AND_LOGISTICS: "marketing_distribution_and_logistics",
	STEM: "stem",
	TRANSPORTATION: "transportation",
} as const satisfies Record<string, DBStudentCareerCluster>;

export type StudentCareerCluster = (typeof StudentCareerCluster)[keyof typeof StudentCareerCluster];

type _EnsureAllDBValues = Exclude<DBStudentCareerCluster, StudentCareerCluster> extends never ? true : never;
const _check: _EnsureAllDBValues = true;
void _check;

export const STUDENT_CAREER_CLUSTERS = Object.values(StudentCareerCluster) as readonly StudentCareerCluster[];

export const studentCareerClusterSchema = z.enum(StudentCareerCluster);

export const STUDENT_CAREER_CLUSTER_LABELS: Record<StudentCareerCluster, string> = {
	all: "All",
	advanced_manufacturing: "Advanced Manufacturing",
	advanced_manufacturing_architecture_and_construction: "Advanced Manufacturing, Architecture & Construction",
	adv_manufacturing_and_ag_food_natural_resources: "Advanced Manufacturing, and Agriculture, Food & Natural Resources",
	adv_manufacturing_ag_food_nat_res_arch_construction_transport:
		"Advanced Manufacturing; Agriculture, Food & Natural Resources; Architecture & Construction; and Transportation",
	adv_manufacturing_ag_food_nat_res_and_arch_construction:
		"Advanced Manufacturing; Agriculture, Food & Natural Resources; and Architecture & Construction",
	agriculture_food_and_natural_resources: "Agriculture, Food & Natural Resources",
	ag_food_natural_resources_and_architecture_construction: "Agriculture, Food & Natural Resources, and Architecture & Construction",
	ag_food_natural_resources_and_business_mgmt_admin: "Agriculture, Food & Natural Resources, and Business Management & Administration",
	ag_food_nat_res_law_safety_corrections_security_transportation:
		"Agriculture, Food & Natural Resources; Law, Public Safety, Corrections, & Security; and Transportation",
	architecture_and_construction: "Architecture & Construction",
	architecture_and_construction_and_stem: "Architecture & Construction, and STEM",
	arts_audio_visual_technology_and_communications: "Arts, Audio/Visual Technology, & Communications",
	business_management_and_administration: "Business Management & Administration",
	business_management_and_administration_and_finance: "Business Management & Administration, and Finance",
	business_mgmt_admin_and_marketing_dist_logistics: "Business Management & Administration, and Marketing, Distribution & Logistics",
	business_mgmt_admin_finance_and_marketing_dist_logistics:
		"Business Management & Administration; Finance; and Marketing, Distribution & Logistics",
	education_and_training: "Education & Training",
	education_and_training_and_health_science: "Education & Training, and Health Science",
	finance: "Finance",
	health_science: "Health Science",
	health_science_and_human_services: "Health Science, and Human Services",
	health_science_and_law_public_safety_corrections_and_security: "Health Science, and Law, Public Safety, Corrections, & Security",
	hospitality_and_tourism: "Hospitality & Tourism",
	hospitality_and_tourism_and_human_services: "Hospitality & Tourism, and Human Services",
	human_services: "Human Services",
	information_technology: "Information Technology",
	law_public_safety_corrections_and_security: "Law, Public Safety, Corrections, & Security",
	marketing_distribution_and_logistics: "Marketing, Distribution & Logistics",
	stem: "STEM",
	transportation: "Transportation",
};
